import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useClassMembershipStore } from '@/features/class/classMembershipStore';
import { MOCK_CURRENT_USER_PROFILE } from '@/features/profile/mockData';

export interface ClassJoinRequest {
  readonly id: string;
  readonly classId: string;
  readonly classCode: string;
  readonly className: string;
  readonly ownerUsername: string;
  readonly requesterName: string;
  readonly requesterUsername: string;
  readonly requesterInitials: string;
  readonly createdAt: string;
}

export interface ClassAdminInvite {
  readonly id: string;
  readonly classId: string;
  readonly className: string;
  readonly inviterName: string;
  readonly inviterInitials: string;
  readonly createdAt: string;
}

interface SubmitJoinRequestInput {
  readonly classId: string;
  readonly classCode: string;
  readonly className: string;
  readonly ownerUsername: string;
  readonly requesterName: string;
  readonly requesterUsername: string;
  readonly requesterInitials: string;
}

// Join requests submitted locally are mirrored here only for legacy UI state.
// The real outgoing request is created with `POST /midl/api/v3/board/join`,
// and actionable incoming rows come from `GET /midl/api/v3/board/join/request`.
const SEED_JOIN_REQUESTS: readonly ClassJoinRequest[] = [] as const;

// Admin invites are now sourced live from `GET /midl/api/v3/board/join/request`
// (rows where `isAdmin === 1`). The local store keeps its slice for legacy
// callers but seeds nothing.
const SEED_ADMIN_INVITES: readonly ClassAdminInvite[] = [] as const;

interface ClassRequestsState {
  joinRequests: ClassJoinRequest[];
  adminInvites: ClassAdminInvite[];
  submitJoinRequest: (input: SubmitJoinRequestInput) => void;
  approveJoinRequest: (requestId: string) => void;
  declineJoinRequest: (requestId: string) => void;
  withdrawJoinRequest: (classId: string, requesterUsername: string) => void;
  approveAdminInvite: (inviteId: string) => void;
  declineAdminInvite: (inviteId: string) => void;
  getPendingJoinRequestsForOwner: (ownerUsername: string) => ClassJoinRequest[];
  getPendingAdminInvites: () => ClassAdminInvite[];
}

let nextJoinRequestId = 100;

export const useClassRequestsStore = create<ClassRequestsState>()(
  persist(
    (set, get) => ({
      joinRequests: [...SEED_JOIN_REQUESTS],
      adminInvites: [...SEED_ADMIN_INVITES],

      submitJoinRequest: (input) => {
        const duplicate = get().joinRequests.some(
          (item) =>
            item.classId === input.classId &&
            item.requesterUsername.toLowerCase() === input.requesterUsername.toLowerCase(),
        );
        if (duplicate) {
          return;
        }

        nextJoinRequestId += 1;
        const request: ClassJoinRequest = {
          id: `join-req-${nextJoinRequestId}`,
          classId: input.classId,
          classCode: input.classCode,
          className: input.className,
          ownerUsername: input.ownerUsername,
          requesterName: input.requesterName,
          requesterUsername: input.requesterUsername,
          requesterInitials: input.requesterInitials,
          createdAt: new Date().toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
          }),
        };

        set((state) => ({
          joinRequests: [request, ...state.joinRequests],
        }));
      },

      approveJoinRequest: (requestId) => {
        const request = get().joinRequests.find((item) => item.id === requestId);
        if (!request) {
          return;
        }

        if (
          request.requesterUsername.toLowerCase() ===
          MOCK_CURRENT_USER_PROFILE.username.toLowerCase()
        ) {
          useClassMembershipStore.getState().grantMembership(request.classId);
        }

        set((state) => ({
          joinRequests: state.joinRequests.filter((item) => item.id !== requestId),
        }));
      },

      declineJoinRequest: (requestId) => {
        set((state) => ({
          joinRequests: state.joinRequests.filter((item) => item.id !== requestId),
        }));
      },

      withdrawJoinRequest: (classId, requesterUsername) => {
        const normalized = requesterUsername.toLowerCase();
        set((state) => ({
          joinRequests: state.joinRequests.filter(
            (item) =>
              !(item.classId === classId && item.requesterUsername.toLowerCase() === normalized),
          ),
        }));
      },

      approveAdminInvite: (inviteId) => {
        set((state) => ({
          adminInvites: state.adminInvites.filter((item) => item.id !== inviteId),
        }));
      },

      declineAdminInvite: (inviteId) => {
        set((state) => ({
          adminInvites: state.adminInvites.filter((item) => item.id !== inviteId),
        }));
      },

      getPendingJoinRequestsForOwner: (ownerUsername) =>
        get().joinRequests.filter(
          (item) => item.ownerUsername.toLowerCase() === ownerUsername.toLowerCase(),
        ),

      getPendingAdminInvites: () => get().adminInvites,
    }),
    // Bumped when seed data is removed so previously persisted demo rows
    // (e.g. the "Jordan Lee" join request or "Jennifer Torres" admin invite)
    // are dropped on next load.
    { name: 'myu-class-requests-v3' },
  ),
);
